import React, { useRef, useEffect, useState, useContext, useCallback, useLayoutEffect, useMemo } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Context from '../../context';
import Container from '../global/Container';
import './index.scss';
import Popup from './Popup';
import MapNav from './MapNav';
import PoiCarousel from './PoiCarousel';
import useKeypress from '../../hooks/useKeypress';
import { toggleAllPoiCategoryAndPoiType } from "../../helpers";

import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import {
  addPoi, addPoiType, createLocationMarkerElement, debounce,
  deactivateAllPoisAndCategories, focusOnPoiType, groupingPoisWithSameCoordinates, safelyRemoveAllMarkers,
  toggleAllPoiTypeLayersExceptActive, toggleAllPoiTypeMarkersExceptActive
} from "./helpers";
import { useClusterUpdater } from "../../hooks/useClusterUpdater";
import { useMarkerVisibility } from "../../hooks/useMarkerVisibility";
mapboxgl.accessToken = 'pk.eyJ1IjoiaGFsc3RlYWQiLCJhIjoiY2tsdGE0dzB4MXVnazJwcW1kNmJ2bzFyNSJ9.bnNuaLXB89x8RQtXQ--oRw';

const Map = (props) => {
  const {
    itin: itinerary,
    icons,
    viewMode,
    previewMode,
    globalMapSettings,
    setGlobalMapSettings,
    setScreenNameOverride,
    setHeaderColorOverrideClass,
    customHistory,
    removeItemsFromCustomHistory,
    activateAllPoisFromPoiList,
    setActivateAllPoisFromPoiList,
    setBackPathOverride,
    cameFromPoiListFlag, setCameFromPoiListFlag,
  } = useContext(Context);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { previewLinkId, ...rest } = useParams();
  const poiId = parseInt(rest.poiId);
  const slideNum = parseInt(rest.slideNum);

  const isDayMap = props.type === 'daily';
  const pinnedPoiIdsRef = useRef(new Set());

  const [localMapSettings, setLocalMapSettings] = useState(() => {
    if (!isDayMap || !globalMapSettings) return null;

    const settings = JSON.parse(JSON.stringify(globalMapSettings));
    const dayPois = props.dayMapConfig?.pois;

    // dayPois null/absent → keep global defaults; [] → all off; [ids] → all off with pinned POIs
    if (dayPois != null) {
      deactivateAllPoisAndCategories(settings);
      pinnedPoiIdsRef.current = new Set(dayPois);
    }

    return settings;
  });

  const effectiveMapSettings = isDayMap ? localMapSettings : globalMapSettings;
  const setEffectiveMapSettings = isDayMap ? setLocalMapSettings : setGlobalMapSettings;

  const clearPinsForCategory = useCallback((categoryId) => {
    const pins = pinnedPoiIdsRef.current;
    if (!pins.size) return;
    const pois = effectiveMapSettings?.pois;
    if (!pois) return;
    const numCatId = parseInt(categoryId);
    for (const id in pois) {
      if (parseInt(pois[id].iconId) === numCatId) {
        pins.delete(parseInt(id));
        pois[id].pois?.forEach(p => pins.delete(p.id));
      }
    }
  }, [effectiveMapSettings?.pois]);

  const clearPinById = useCallback((poiId) => {
    const pins = pinnedPoiIdsRef.current;
    if (!pins.size) return;
    pins.delete(poiId);
    const poi = effectiveMapSettings?.pois?.[poiId];
    poi?.pois?.forEach(p => pins.delete(p.id));
  }, [effectiveMapSettings?.pois]);

  const showInMapsLocations = useMemo(() => {
    if (!itinerary?.locations) return [];

    const seen = new Set();
    const unique = [];

    for (const loc of itinerary.locations) {
      if (loc.showInMaps) {
        const key = `${loc.latitude},${loc.longitude}`;
        if (!seen.has(key)) {
          seen.add(key);
          unique.push(loc);
        }
      }
    }

    return unique;
  }, [itinerary?.locations]);

  let focusLocation;
  if (poiId) {
    const combinedMapItems = [
      ...(itinerary?.pois ?? []),
      ...(itinerary?.tracks ?? []),
      ...(itinerary?.routes ?? [])
    ];
    const nestedPois = combinedMapItems.reduce(
      (prev, curr) => {
        if (curr.pois?.length > 0) prev = [...curr.pois];
        return prev;
      }, []);
    focusLocation = [...combinedMapItems, ...nestedPois, ...Object.values(effectiveMapSettings?.connectedLocations ?? {})].find(x => x.id === poiId);
  } else if (props.locationId && !isDayMap) {
    focusLocation = showInMapsLocations.find(x => x.id === props.locationId);
  }

  const initialBoundsPoints = useMemo(() => {
    if (!props.dayMapConfig) return [];
    const points = [];

    // Day-specific locations for initial camera focus
    if (props.dayMapConfig.locations?.length) {
      const locIdSet = new Set(props.dayMapConfig.locations);
      for (const loc of itinerary?.locations ?? []) {
        if (locIdSet.has(loc.id) && loc.showInMaps && loc.latitude != null && loc.longitude != null) {
          points.push({ latitude: loc.latitude, longitude: loc.longitude });
        }
      }
    }

    // Day-specific POIs for initial camera focus
    if (props.dayMapConfig.pois?.length) {
      const allPois = [
        ...(itinerary?.pois ?? []),
        ...(itinerary?.tracks ?? []),
        ...(itinerary?.routes ?? []),
      ];
      const nestedPois = allPois.flatMap(p => p.pois ?? []);
      const poiIdSet = new Set(props.dayMapConfig.pois);
      for (const p of [...allPois, ...nestedPois]) {
        if (!poiIdSet.has(p.id)) continue;
        if (p.waypoints?.length) {
          for (const wp of p.waypoints) {
            if (wp.latitude != null && wp.longitude != null) {
              points.push({ latitude: wp.latitude, longitude: wp.longitude });
            }
          }
        } else if (p.latitude != null && p.longitude != null) {
          points.push({ latitude: p.latitude, longitude: p.longitude });
        }
      }
    }

    return points;
  }, [props.dayMapConfig, itinerary?.locations, itinerary?.pois, itinerary?.tracks, itinerary?.routes]);

  const lng = focusLocation?.longitude ?? initialBoundsPoints?.[0]?.longitude ?? showInMapsLocations?.[0]?.longitude ?? -70.9;
  const lat = focusLocation?.latitude ?? initialBoundsPoints?.[0]?.latitude ?? showInMapsLocations?.[0]?.latitude ?? 42.35;
  const zoom = 13;

  const [map, setMap] = useState(null);
  const [locationMarkers, setLocationMarkers] = useState(null);
  const [satelliteViewActive, setSatelliteViewActive] = useState(false);
  const [showLabelsOverride, setShowLabelsOverride] = useState(!!props.locationId);
  const [emptyContainerHeight, setEmptyContainerHeight] = useState(null);
  const [closePath, setClosePath] = useState(null);
  const [isShowAllPressed, setIsShowAllPressed] = useState(false);
  const [isPoiTypeClicked, setIsPoiTypeClicked] = useState(false);

  const mapContainer = useRef(null);
  const poiMarkers = useRef({});
  const poiTypeMarkers = useRef({});
  const prevSat = useRef(satelliteViewActive);
  const mapRef = useRef(null);
  const poiIdRef = useRef(poiId);
  useEffect(() => {
    poiIdRef.current = poiId;
  }, [poiId]);

  const updateMarkerVisibility = useMarkerVisibility(map, poiIdRef, poiMarkers,);
  const updateClusters = useClusterUpdater(map, poiIdRef, updateMarkerVisibility);

  const debouncedUpdate = useMemo(() => debounce(updateMarkerVisibility, 50), [updateMarkerVisibility]);
  const { displayedPois, displayedPoiTypes } = useMemo(() => {
    const pois = effectiveMapSettings?.pois ?? {};
    const poiCategories = effectiveMapSettings?.poiCategories ?? {};
    const shiftedGroupedPois = groupingPoisWithSameCoordinates(effectiveMapSettings?.pois ?? []);

    const shiftedPoiById = {};
    shiftedGroupedPois.flat().forEach((poi) => {
      // assign new POI (already a clone from grouping func)
      shiftedPoiById[poi.id] = poi;
    });

    const displayedPois = {};
    const displayedPoiTypes = {};
    const pins = pinnedPoiIdsRef.current;

    Object.values(pois).forEach((poi) => {
      const category = poiCategories[poi.iconId];
      const poiWithShift = shiftedPoiById[poi.id] ?? poi;
      const isTrackOrRoute = poiWithShift.type === 'track' || poiWithShift.type === 'route';

      if (category?.active) {
        if (isTrackOrRoute) {
          if (poiWithShift.active) {
            displayedPoiTypes[poiWithShift.id] = poiWithShift;
            if (poiWithShift?.pois) {
              poiWithShift.pois.forEach((poi) => {
                if (poi.active) displayedPois[poi.id] = poi;
              });
            }
          }
        } else {
          displayedPois[poiWithShift.id] = poiWithShift;
        }
      } else if (pins.size > 0) {
        if (isTrackOrRoute) {
          if (pins.has(poi.id)) {
            displayedPoiTypes[poiWithShift.id] = poiWithShift;
            poiWithShift.pois?.forEach(p => {
              if (pins.has(p.id)) displayedPois[p.id] = p;
            });
          }
        } else if (pins.has(poi.id)) {
          displayedPois[poiWithShift.id] = poiWithShift;
        }
      }
    });

    return { displayedPois, displayedPoiTypes };
  }, [effectiveMapSettings?.poiCategories, effectiveMapSettings?.pois]);

  useEffect(() => {
    setScreenNameOverride(poiId ? '' : itinerary?.menu?.find(x => x.segue === 'maps')?.label);
    return () => {
      setScreenNameOverride(null);
      setBackPathOverride(null);
    };
  }, [setBackPathOverride, itinerary?.menu, setScreenNameOverride, poiId]);

  useEffect(() => {
    if (viewMode === 'mobile') {
      setHeaderColorOverrideClass('color-black');
    }

    return () => setHeaderColorOverrideClass(null);
  }, [setHeaderColorOverrideClass, viewMode]);

  // If there is a non-poi/track location we want to center the map on, we use the override values for showing labels.
  // We always initially want to show labels. The user can toggle them, but we don't want to update the global value for it.
  const getShowLabelsVal = useCallback(() => {
    return props.locationId ? showLabelsOverride : effectiveMapSettings.showLocationMarkerLabels;
  }, [effectiveMapSettings.showLocationMarkerLabels, props.locationId, showLabelsOverride]);

  const closeAllPopups = useCallback(() => {
    const allMarkers = [
      ...Object.values(locationMarkers || {}),
      ...Object.values(poiMarkers.current),
      ...Object.values(poiTypeMarkers.current),
    ];
    for (const m of allMarkers) {
      const p = m.getPopup?.();
      if (p?.isOpen()) m.togglePopup();
    }
  }, [locationMarkers]);

  const updateShowLabelsVal = useCallback(() => {
    if (props.locationId) {
      setShowLabelsOverride(showLabelsOverride => !showLabelsOverride);
      return;
    }

    closeAllPopups();
    setEffectiveMapSettings(prev => ({
      ...prev,
      showLocationMarkerLabels: !prev?.showLocationMarkerLabels
    }));
  }, [props.locationId, setEffectiveMapSettings, closeAllPopups]);

  useEffect(() => {
    if (mapRef.current) return; // initialize map only once

    const options = {
      container: mapContainer.current,
      style: satelliteViewActive ? 'mapbox://styles/mapbox/satellite-streets-v11' : 'mapbox://styles/mapbox/streets-v11',
      attributionControl: false,
    };

    // Day maps zoom to day-specific locations/POIs; global maps zoom to all showInMaps locations
    const cameraBoundsPoints = initialBoundsPoints.length ? initialBoundsPoints : showInMapsLocations;

    if (cameraBoundsPoints.length < 2 || focusLocation) {
      options.center = [lng, lat];
      options.zoom = zoom;
    } else {
      const bounds = {
        west: lng,
        south: lat,
        east: lng,
        north: lat
      };

      for (const { latitude, longitude } of cameraBoundsPoints) {
        if (bounds.north < latitude) bounds.north = latitude;
        if (bounds.south > latitude) bounds.south = latitude;
        if (bounds.west > longitude) bounds.west = longitude;
        if (bounds.east < longitude) bounds.east = longitude;
      }

      options.bounds = Object.values(bounds);
      options.fitBoundsOptions = {
        padding: { bottom: 200, left: 100, right: 100, top: viewMode === 'mobile' ? 200 : 100 }
      }
    }
    const mapInstance = new mapboxgl.Map(options);
    mapRef.current = mapInstance;
    setMap(mapInstance);
  }, [satelliteViewActive, showInMapsLocations, initialBoundsPoints, lat, lng, focusLocation, viewMode, zoom]);

  //add zoom and full screen buttons to the map
  useEffect(() => {
    if (!map || viewMode === 'mobile') return;
    map.addControl(new mapboxgl.FullscreenControl({ container: document.querySelector('.map') }));
    map.addControl(new mapboxgl.NavigationControl());
  }, [map, props.type, viewMode])

  useEffect(() => {
    if (/\/\d+$/.test(pathname) && emptyContainerHeight && mapContainer && focusLocation) {
      const offsetValue = (mapContainer.current.offsetHeight / 2) - (emptyContainerHeight / 2)

      map?.fitBounds([
        [focusLocation.longitude, focusLocation.latitude],
        [focusLocation.longitude, focusLocation.latitude]
      ], {
        maxZoom: 13,
        offset: [0, -(offsetValue / 2)],
        duration: 500,
      })
    }
  }, [pathname, focusLocation, map, emptyContainerHeight, mapContainer])

  const generateMarker = useCallback((type, data, markerLabel = null, markerColor = null) => {
    let el;
    if (type === 'location') {
      el = createLocationMarkerElement(markerLabel, markerColor);
    } else {
      el = document.createElement('DIV');
      el.style.backgroundImage = 'url(' + icons[data.iconId] + ')';
      if (data.type === 'poi') el.style.display = 'none';
    }
    el.className = 'marker marker-' + type;

    const marker = new mapboxgl.Marker(el);

    marker.setLngLat([data.longitude, data.latitude]);

    const popup = new mapboxgl.Popup({
      offset: 25,
      closeButton: false,
      closeOnClick: false,
      className: `popup popup-${type}`,
      focusAfterOpen: false,
    });

    let onInfoBtnClick;
    if (type === 'location') {
      const generatePopupInfoPath = (data) => {
        // for non Stays location
        if (!data.nesting) {
          const locationNode = itinerary.documents?.find(doc => doc.id === data.pdfNodeId);
          return locationNode?.file?.httpsUrl || locationNode?.remoteUrl;
        }

        if (previewMode) {
          let path = window.location.origin + '/preview/';
          if (data.nesting.previewLinkId) path += data.nesting.previewLinkId;
          else path += btoa(`${data.nesting.operatorCode}-${data.nesting.referenceCode}`);
          path += '?branding=' + itinerary.operatorInfo.code;
          return path; // this path will redirect to the link like this: https://{itinerary.operatorInfo.code}.stays.vamoos.com/{data.nesting.operatorCode}/
        }
      };
      const popupInfoUrl = generatePopupInfoPath(data);
      if (popupInfoUrl) onInfoBtnClick = () => {
        window.open(popupInfoUrl, '_blank')}
    }
    if (!onInfoBtnClick) {
      let path = (!previewLinkId && props.type === 'daily')
        ? `/${itinerary.localData?.urlKey}/daily/${slideNum}/maps`
        : `/${itinerary.localData?.urlKey}/maps`
      if (previewLinkId) {
        const pathStart = `/${pathname.match(new RegExp(`^/(preview.*)/${previewLinkId}`))[1]}`;
        if (pathStart === '/preview-maps') {
          path = `${pathStart}/${previewLinkId}`;
        } else {
          path = `${pathStart}/${previewLinkId}/${slideNum}/maps`;
        }
      }
      if (type === 'location' && !data.nesting) { // for location without attachments
        onInfoBtnClick = null;
      } else {
        onInfoBtnClick = () => navigate(`${path}/${data.id}`);
      }
    }
    const hideInfo = !onInfoBtnClick || (type !== 'location' && !data?.longDescription && !data?.documentNodeId)
    popup.setHTML(ReactDOMServer.renderToString(<Popup name={type === 'location' ? data.name : data.infoLabel} hideInfo={hideInfo}/>));

    popup.on('open', (el) => {
      const element = el.target.getElement();
      element.addEventListener('click', (e) => e.stopPropagation());
      element.querySelector('.leftButton')?.addEventListener('click', () => {
        window.open(`https://www.google.com/maps/dir/?api=1&destination=${data.latitude},%20${data.longitude}&dir_action=navigate`, '_blank');
      });
      element.querySelector('.rightButton')?.addEventListener('click', (e) => {
        e.stopPropagation();
        e.cancelBubble = true;
        onInfoBtnClick();
      });
    });
    marker.setPopup(popup);
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      marker.togglePopup();
    });
    return marker;
  }, [itinerary.documents, slideNum, props.type, icons, itinerary.localData?.urlKey, itinerary.operatorInfo.code, previewMode, navigate, pathname, previewLinkId]);

  useEffect(() => {
    if (!map) return;
    const global = document.documentElement;
    const accentColor = getComputedStyle(global).getPropertyValue('--accent-color').trim().slice(1);
    const markers = {};
    const showNumbersOnMarkers = showInMapsLocations.length > 1;
    for (let i = 0, n = showInMapsLocations.length; i < n; i++) {
      const l = showInMapsLocations[i];
      const marker = generateMarker('location', l, showNumbersOnMarkers ? (i + 1) : '', accentColor);
      marker.addTo(map);
      markers[l.id] = marker;
    }
    setLocationMarkers(markers);
  }, [generateMarker, showInMapsLocations, map]);

  const runMapOverlays = useCallback(() => {
    if (!map || itinerary?.mapOverlays?.length === 0) return;

    const allLayers = map.getStyle().layers;
    const firstSymbolLayer = allLayers.find(l => ["symbol", "circle", "heatmap"].includes(l.type));

    itinerary?.mapOverlays?.forEach(overlay => {
      const imageUrl = itinerary.documents.find(doc => doc.id === overlay.imageId)?.file?.httpsUrl;
      const { topLeft, topRight, bottomRight, bottomLeft } = overlay.coordinates;
      const coords = [
        [topLeft.longitude, topLeft.latitude],
        [topRight.longitude, topRight.latitude],
        [bottomRight.longitude, bottomRight.latitude],
        [bottomLeft.longitude, bottomLeft.latitude],
      ];

      const sourceId = `overlay-image-${overlay.imageId}`;
      const layerId = `image-${overlay.imageId}`;
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);

      map.addSource(sourceId, {
        type: "image",
        url: imageUrl,
        coordinates: coords,
      });

      map.addLayer({
        id: layerId,
        source: sourceId,
        type: "raster",
        paint: { "raster-opacity": 1 }
      }, firstSymbolLayer?.id); // to put overlay below the other layers
    })
  }, [itinerary.documents, itinerary?.mapOverlays, map])

  useEffect(() => {
      if (!map || itinerary?.mapOverlays?.length === 0) return;
      map.once('styledata', runMapOverlays)
  }, [itinerary?.mapOverlays?.length, map, runMapOverlays])

  useEffect(() => {
    if (!map) return;

    map.on('zoomend', updateMarkerVisibility);
    map.on('move', debouncedUpdate);
    map.once('idle', updateMarkerVisibility);

    return () => {
      map.off('zoomend', updateMarkerVisibility);
      map.off('idle', updateMarkerVisibility);
      map.off('move', debouncedUpdate);
    };
  }, [debouncedUpdate, map, updateMarkerVisibility]);

  useEffect(() => {
    if (!effectiveMapSettings) return;
    if (!locationMarkers) return;
    if (!map) return;
    const handleLocationMarkerLabels = (poiId) => {
      const activeLocation = poiId ? showInMapsLocations.find(x => x.id === poiId) : null;
      const showLocationMarkerLabels = activeLocation ? props.type !== 'daily' : getShowLabelsVal();
      for (const m of Object.values(locationMarkers)) {
        const p = m.getPopup();
        if (!p) continue;
        const isOpen = p.isOpen();
        if ((showLocationMarkerLabels && isOpen) || (!showLocationMarkerLabels && !isOpen)) continue;
        m.togglePopup();
      }
      if (!poiId) {
        for (const markers of [poiMarkers.current, poiTypeMarkers.current]) {
          for (const m of Object.values(markers)) {
            if (m.getElement?.()?.style.display === 'none') continue;
            const p = m.getPopup?.();
            if (!p) continue;
            const isOpen = p.isOpen();
            if ((showLocationMarkerLabels && isOpen) || (!showLocationMarkerLabels && !isOpen)) continue;
            m.togglePopup();
          }
        }
      }
    };
    // Initial handling of location marker labels must be done this way. Otherwise it will cause the daily section to scroll
    // to the left when the labels are initially opened.
    map.on('load', () => handleLocationMarkerLabels(poiId));
    handleLocationMarkerLabels(poiId);
    const onIdle = () => handleLocationMarkerLabels(poiId);
    map.once('idle', onIdle);

    return () => {
      map.off('load', handleLocationMarkerLabels);
      map.off('idle', onIdle);
    };
  }, [props.type, getShowLabelsVal, effectiveMapSettings, locationMarkers, map, poiId, showInMapsLocations]);

  // init render of clusters and markers
  useEffect(() => {
    if (!map) return;
    let cleanup;

    map.once('style.load', () => {
      cleanup = updateClusters(Object.values(displayedPois));
      const waitForLayer = () => {
        if (map.getLayer('unclustered-point')) {
          updateMarkerVisibility(true);
        } else {
          requestAnimationFrame(waitForLayer);
        }
      };
      waitForLayer();
    });

    return () => {
      if (cleanup) cleanup();
    };

    // adding displayedPois to the current useEffect array of dependencies will cause multiple re-renders each time displayedPois changes.
    // to disable eslint warn and do not add isLoggedIn in array of dependency use next line
    // eslint-disable-next-line
  }, [updateClusters, map, satelliteViewActive, updateMarkerVisibility]);

  // toggle satelliteViewActive
  useEffect(() => {
    let cleanup;
    if (satelliteViewActive !== prevSat.current) {
      prevSat.current = satelliteViewActive;
      if (!map || !map.getStyle || !map.isStyleLoaded?.()) return;

      safelyRemoveAllMarkers(poiMarkers);
      safelyRemoveAllMarkers(poiTypeMarkers);
      Object.values(displayedPois).forEach(x => addPoi(x, null, poiId, effectiveMapSettings, poiMarkers, generateMarker, map, pinnedPoiIdsRef.current));

      map.setStyle(satelliteViewActive ? 'mapbox://styles/mapbox/satellite-streets-v11' : 'mapbox://styles/mapbox/streets-v11');
      map.once('idle', () => {
        cleanup = updateClusters(Object.values(displayedPois), updateMarkerVisibility, true);
      });

      const waitForLayer = () => {
        if (map.getLayer('unclustered-point')) {
          requestAnimationFrame(() => {
            updateMarkerVisibility();
            requestAnimationFrame(() => {
              map.once('idle', () => {
                Object.values(displayedPoiTypes).forEach(poi => addPoiType(poi, map, generateMarker, poiTypeMarkers, effectiveMapSettings, poiMarkers, null, pinnedPoiIdsRef.current));
                runMapOverlays();
              });
            });
          });
        } else {
          requestAnimationFrame(waitForLayer);
        }
      };
      waitForLayer();
    }

    return () => {
      if (cleanup) cleanup();
    };
  }, [displayedPois, displayedPoiTypes, generateMarker, updateClusters, map, satelliteViewActive, updateMarkerVisibility, poiId, effectiveMapSettings, runMapOverlays]);

  const activeItem = () => {
    if (!effectiveMapSettings.pois[poiId]) {
      //if active POI id is undefined in globalMapSettings.pois, looking for the active POI id in the nested POIs
      const poisWithNestedPois = Object.values(effectiveMapSettings.pois).filter(x => x.pois?.length)
      let out
      for (let i = 0; i < poisWithNestedPois.length; i++) {
        for (let j = 0; j < poisWithNestedPois[i].pois.length; j++) {
          if (poisWithNestedPois[i].pois[j].id === poiId) out = poisWithNestedPois[i].pois[j]
        }
      }
      if (!out) out = effectiveMapSettings.connectedLocations[poiId];
      return out
    } else {
      return effectiveMapSettings.pois[poiId]
    }
  }

  //remove poiType layer if it remains after renderer
  const removeLayer = (typeId) => {
    if (map.getLayer('poi' + typeId)) {
      map.removeLayer('poi-outline-' + typeId);
      map.removeLayer('poi' + typeId);
      map.removeSource('poi-type-' + typeId);
    }
  }

  //exit Poi Carousel, go to the main map and hide the poi carousel
  const exitPoiCarousel = useCallback(() => {
    if (!poiId) return;
    let newPath = pathname.split('/').filter(x => x);
    newPath.pop();
    newPath = '/' + newPath.join('/');

    let backPath;
    let i = 0;
    let target;
    while (true) {
      target = customHistory[customHistory.length + i - 1];
      if (!target || !target.startsWith(newPath)) {
        backPath = newPath;
        break;
      }
      if (target.match(new RegExp('^' + newPath + '[/]?$', 'i'))) {
        backPath = i;
        break;
      }
      i--;
    }
    navigate(newPath);
    removeItemsFromCustomHistory(backPath);

    updateClusters(Object.values(displayedPois), updateMarkerVisibility);
    toggleAllPoiTypeLayersExceptActive(map, Object.values(displayedPoiTypes), true, null);
    toggleAllPoiTypeMarkersExceptActive(map, poiTypeMarkers, true, null);
  }, [displayedPoiTypes, map, poiId, pathname, navigate, removeItemsFromCustomHistory, updateClusters, displayedPois, updateMarkerVisibility, customHistory]);

  useKeypress('Escape', [navigate, itinerary], () => exitPoiCarousel());

  useLayoutEffect(() => {
    if (viewMode === 'desktop') return;
    let backPath = null;
    const isDaily = props.type === 'daily';

      let newPath = pathname.split('/').filter(x => x !== '');
      const newPoiPath = '/' + newPath[0] + '/pois';
      newPath.pop();
      newPath = '/' + newPath.join('/');

      let i = 0;
      let target;
      while (true) {
        target = customHistory[customHistory.length + i - 1];
        if (!target) {
          backPath = `/${itinerary.localData?.urlKey}`;
          break;
        }
        if (!isDaily) {
          // if user navigate from POI list, set back path to POI list
          if (target.match(new RegExp(`/\\d+/pois[/]?$`, 'i'))) {
            backPath = (customHistory.lastIndexOf(newPoiPath) - (customHistory.length - 1));
            break;
          }
          // if user navigate from daily maps, set back path to daily section
          if (target.match(new RegExp(`/daily/\\d+/maps[/]?$`, 'i'))) {
            backPath = i - 1;
            break;
          }
          if (target.match(new RegExp('^' + newPath + '[/]?$', 'i'))) {
            backPath = i;
            break;
          }
        }

        if (isDaily && target.match(new RegExp('^' + newPath + '[/]?$', 'i'))) {
          break;
        }
        // If user navigates to maps from Accommodation menu item, set back path to daily section
        if (isDaily && target.match(new RegExp(`/daily/\\d+/?$`, 'i'))) {
          setBackPathOverride(i);
          break;
        }
        i--;
      }

    // To fix issue with navigation.
    // When user will toggle location labels then navigation to -number did not work properly
    if (typeof backPath === 'number') backPath = customHistory[customHistory?.length + (backPath - 1)] ?? '/';

    setClosePath(backPath);
  }, [setBackPathOverride, pathname, customHistory, props.type, itinerary.localData?.urlKey, viewMode]);

  const runUpdatePoi = useCallback(() => {
    if (!map) return;

    if (map.isStyleLoaded?.()) updateClusters(Object.values(displayedPois), updateMarkerVisibility);

    Object.values(displayedPois).forEach(x => addPoi(x, null, poiId, effectiveMapSettings, poiMarkers, generateMarker, map, pinnedPoiIdsRef.current));

    Object.values(displayedPoiTypes).forEach(poi => {
      const key = poi.id.toString();
      const isNew = !poiTypeMarkers.current[key];
      if (isNew) {
        if (isPoiTypeClicked) {
          focusOnPoiType(poi, map, isShowAllPressed);
          addPoiType(poi, map, generateMarker, poiTypeMarkers, effectiveMapSettings, poiMarkers, null, pinnedPoiIdsRef.current)
        } else {
          map.once('idle', () => addPoiType(poi, map, generateMarker, poiTypeMarkers, effectiveMapSettings, poiMarkers, null, pinnedPoiIdsRef.current));
        }
      }
    });

    Object.keys(poiTypeMarkers.current).forEach(id => {
      const stillExists = displayedPoiTypes[id];
      if (!stillExists) {
        if (map.getLayer('poi' + id)) {
          map.removeLayer('poi-outline-' + id);
          map.removeLayer('poi' + id);
          map.removeSource('poi-type-' + id);
        }

        const sourceId = `poi-type-${id}`;
        if (map.getSource(sourceId)) {
          map.removeSource(sourceId);
        }

        const entry = poiTypeMarkers.current[id];
        if (!!entry) entry.remove();

        delete poiTypeMarkers.current[id];
      }
    });
    setCameFromPoiListFlag(false);
  }, [setCameFromPoiListFlag, displayedPoiTypes, map, displayedPois, updateClusters, updateMarkerVisibility, poiId, effectiveMapSettings, generateMarker, isShowAllPressed, isPoiTypeClicked]);

  const runTogglePoiVisibility = useCallback((poiId) => {
    toggleAllPoiTypeLayersExceptActive(map, Object.values(displayedPoiTypes), false, poiId);
    toggleAllPoiTypeMarkersExceptActive(map, poiTypeMarkers, false, poiId);
  }, [displayedPoiTypes, map])

  useEffect(() => {
    runUpdatePoi();
  }, [displayedPois, runUpdatePoi, displayedPoiTypes]);

  useEffect(() => {
    if (!poiId || !map) return
    const key = poiId.toString();

    if (cameFromPoiListFlag) {
      map.once('idle', () => runTogglePoiVisibility(key));
    } else{
      map.once('moveend', () => runTogglePoiVisibility(key));
    }
  }, [map, poiId, runTogglePoiVisibility, cameFromPoiListFlag]);

  //set All Poi Categories Not Active for mobile view
  // if user navigate from POI list to maps and all categories was not active and user navigate back to POI list without changing active poi categories
  const setAllPoiCategoriesNotActive = () => {
    if (isDayMap) return;
    if (!activateAllPoisFromPoiList) return;
    setActivateAllPoisFromPoiList(false);
    const { newPois, categories } = toggleAllPoiCategoryAndPoiType(globalMapSettings, false);
    setGlobalMapSettings({
      ...globalMapSettings,
      pois: newPois,
      poiCategories: categories
    });
  }

  const renderContent = () => {
    //sorting POIs by type
    const activeItems = Object.values(effectiveMapSettings.pois).reduce(
      (prev, curr) => {
        if (curr.type === 'track' || curr.type === 'route') {
          if (curr.active && curr.pois?.length !== 0) {
            for (let i = 0; i < curr.pois?.length; i++) {
              if (!prev['poi'].some(x => x.id === curr.pois[i].id)) prev['poi'].push(curr.pois[i]); //this is a check to avoid re-adding the poi in case the poi is available separately and added to tracks or routes
            }
          }
          if (curr.active) {
            prev[curr.type].push(curr);
          }
        } else if (curr.type === 'poi' && effectiveMapSettings.poiCategories[curr.iconId].active) {
          if (!prev['poi'].some(x => x.id === curr.id)) prev['poi'].push(curr); //this is a check to avoid re-adding the poi in case the poi is available separately and added to tracks or routes
        }
        return prev;
      },
      { poi: [], track: [], route: [] }
    );
    activeItems.locations = [...Object.values(effectiveMapSettings?.connectedLocations)];

    //Find the type of active POI. Used a separate method instead of a simple find, because we have nested POIs
    const foundSelectedItem = (obj, id) => {
      for (let type in obj) {
        const foundPoi = obj[type].find(item => item.id === id);
        if (foundPoi) {
          return { type, poi: foundPoi };
        }
      }
      return null;
    }

    return (
      <>
        <div
          ref={mapContainer}
          className="map-container"
          onClick={() => {
            poiIdRef.current = undefined;
            exitPoiCarousel();
            if (getShowLabelsVal()) {
              updateShowLabelsVal();
            } else {
              closeAllPopups();
            }
          }}
        />
        {poiId ?
          <PoiCarousel
            data={
              activeItems[foundSelectedItem(activeItems, poiId)?.type] ?? [activeItem()]
            }
            activeItem={activeItem()}
            setEmptyContainerHeight={setEmptyContainerHeight}
            type={props.type}
            slideNum={slideNum}
            disableSwipe={!!poiId}
          /> :
          <MapNav
            satelliteViewActive={satelliteViewActive}
            setSatelliteViewActive={setSatelliteViewActive}
            getShowLabelsVal={getShowLabelsVal}
            updateShowLabelsVal={updateShowLabelsVal}
            removeLayer={removeLayer}
            setIsShowAllPressed={setIsShowAllPressed}
            setIsPoiTypeClicked={setIsPoiTypeClicked}
            mapSettings={effectiveMapSettings}
            setMapSettings={setEffectiveMapSettings}
            clearPinsForCategory={clearPinsForCategory}
            clearPinById={clearPinById}
            pinnedPoiIds={pinnedPoiIdsRef.current}
          />
        }
        {/* { renderPoiCarousel() } */}
      </>
    );
  };

  if (props.type === 'daily') {
    return (
      <div className={`map daily-map`}>
        {renderContent()}
      </div>
    );
  }

  return (
    <Container
      className="container-map"
      mainElementClassName="map"
      closePath={closePath}
      backButtonClickFn={setAllPoiCategoriesNotActive}
    >
      {renderContent()}
    </Container>
  );
};

export default Map;